import { ReactNode, cloneElement, isValidElement, useCallback, forwardRef } from 'react'
import { Checkbox } from '@mui/material'
import { List, CellMeasurer, CellMeasurerCache } from 'react-virtualized'
import { useCacheMeasurer } from './useCacheMeasurer'
import { t } from '@lingui/core/macro'

const cache = new CellMeasurerCache({
  defaultHeight: 33,
  fixedHeight: false,
})

interface Option {
  label: string
  value: string
}

interface Props {
  children?: ReactNode[] | ReactNode
  search?: string
  width?: number
  selected?: Option[]
  options?: Option[]
  role?: string | undefined
  onMouseDown: (_e: any) => ({ isSearching }) => void
  onClick: () => void
}

/**
 * Virtualized list of dropdown menu items.
 * @param {Props} props
 * @param {Props.children} props.children Item
 * @param {Props.search} props.search Search text from text input.
 * @param {Props.width} props.width The width of the ListBox component.
 * @param {Props.options} props.options The full dropdown list. Used for original index referenc.
 * @param {Props.selected} props.selected The selected options.
 * @param {Props.role} props.role ListBox Component role
 * @param {Props.onMouseDown} props.onMouseDown Event to trigger menu item select.
 * @param {Props.onClick} props.onClick Backup event to handle checkbox component.
 */
export const MenuList = forwardRef<HTMLDivElement, Props>(function MenuList(
  {
    children,
    search = '',
    width = 0,
    options,
    selected,
    role,
    onMouseDown,
    onClick,
    ...rest
  }: Props,
  ref,
) {
  const { height } = useCacheMeasurer({ children, search, cache })
  const isChildrenArray = Array.isArray(children)

  const RowRenderer = useCallback(
    ({ index, key, style, parent }) => {
      if (!isChildrenArray || (isChildrenArray && !children.length)) return null

      const elem = children[index]
      const elemChildren = isValidElement(elem) ? elem?.props?.children : [elem]
      const elemKey = String(
        isValidElement(elem) ? elem?.key : typeof elem !== 'object' ? elem : `elem-${index}`,
      )
      const childrenArray = Array.isArray(elemChildren) ? elemChildren : [elemChildren]
      const isSelected = selected?.findIndex(s => s.label === elemChildren) !== -1
      const originalIndex = options?.findIndex(o => o.label === elemChildren)

      const CheckboxComponent = (
        <Checkbox
          key={`${elemKey}-checkbox`}
          checked={isSelected}
          onMouseDown={e => {
            // necessary to prevent close of parent ListBox,
            // and onMouseDown is triggered by parent LI instead.
            e.preventDefault()
            e.stopPropagation()
          }}
          onClick={e => {
            // onMouseDown can cause a double click to be required,
            // this fixes that; by default, events are not spread properly
            // to handle all click situations within ListBoxComponent.
            onMouseDown(e)
          }}
        />
      )

      const newChildren = [CheckboxComponent, ...childrenArray]
      const childElement = isValidElement(elem)
        ? cloneElement(elem, {
            ...(elem.props || {}),
            children: newChildren,
            className: [elem.props.className, isSelected ? '-is-selected' : ''].join(' '),
            style,
            key: `li-${elemKey}-${key}`,
            onMouseDown,
            onClick,
            'data-option-index': originalIndex,
          })
        : null

      return (
        <CellMeasurer
          cache={cache}
          columnIndex={0}
          key={`${elemKey}-${key}`}
          parent={parent}
          rowIndex={index}
          {...(childElement?.props || {})}
        >
          {childElement}
        </CellMeasurer>
      )
    },
    [isChildrenArray, children, selected, cache],
  )

  return (
    <div ref={ref}>
      <div {...rest}>
        <List
          role={role}
          className="menu-list"
          height={height}
          overscanRowCount={10}
          noRowsRenderer={() => <div style={{ padding: 4 }}>{t`No matching rows`}</div>}
          rowCount={(isChildrenArray && children.length) || 0}
          rowHeight={cache.rowHeight}
          rowRenderer={RowRenderer}
          width={width}
        />
      </div>
    </div>
  )
})
